// Circle import removed
import { API_URL, getAuthHeaders } from './config';

export const circlesService = {
    list: async () => {
        const res = await fetch(`${API_URL}/circles`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to list circles");
        const data = await res.json() as any;
        // Backend returns raw array, not { data: [] }
        return Array.isArray(data) ? data : (data.data || []);
    },
    create: async (data: {
        name: string;
        description?: string;
        photo?: string | null;
        share_in_feed?: boolean;
        city?: string;
        country?: string;
        lat?: number;
        lon?: number;
    }) => {
        const res = await fetch(`${API_URL}/circles`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create circle");
        const json = await res.json() as any;
        return json; // Backend returns raw object
    },
    update: async (id: string, data: {
        name?: string;
        description?: string;
        city?: string;
        country?: string;
        lat?: number;
        lon?: number;
    }) => {
        const res = await fetch(`${API_URL}/circles/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update circle");
        return res.json();
    },
    getDetails: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to get circle details");
        }
        const json = await res.json() as any;
        return json; // Backend returns raw object
    },
    join: async (id: string, userId: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/join`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ userId })
        });
        if (!res.ok) throw new Error("Failed to join circle");
        return res.json();
    },
    delete: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to delete circle");
        return res.json();
    },
    getAvailability: async (id: string, date: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/availability?date=${date}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to check availability");
        const json = await res.json() as any;

        // Return valid slots or empty array
        if (json.slots && Array.isArray(json.slots)) {
            return json.slots;
        }

        return Array.isArray(json) ? json : [];
    },
    removeMember: async (id: string, userId: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/members`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ userId })
        });
        if (!res.ok) throw new Error("Failed to remove member");
        return res.json();
    },
    acceptInvite: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/accept`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to accept invite");
        return res.json();
    },
    declineInvite: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/decline`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to decline invite");
        return res.json();
    },
    markRead: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/read`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to mark as read");
        return res.json();
    },
    uploadPhoto: async (id: string, file: Blob) => {
        const buffer = await file.arrayBuffer();
        const res = await fetch(`${API_URL}/circles/${id}/photo`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/octet-stream'
            },
            body: buffer
        });
        if (!res.ok) throw new Error("Failed to upload photo");
        return res.json();
    },
    recruit: async (id: string, data: { spots: number, min_level: number }) => {
        const res = await fetch(`${API_URL}/circles/${id}/recruit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to post recruitment");
        return res.json();
    },
    cancelRecruitment: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/recruit`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to cancel recruitment");
        return res.json();
    },
    apply: async (id: string) => {
        const res = await fetch(`${API_URL}/circles/${id}/apply`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to apply");
        return res.json();
    },
    generateLogo: async (data: { name: string, description?: string, tone: string }) => {
        const res = await fetch(`${API_URL}/circles/generate-logo`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to generate AI logo via backend");
        }
        return res.json() as Promise<{ url: string }>;
    }
};
